import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, CheckCircle2, HelpCircle } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Navbar, goToContact, scrollToSection } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SectionHeading, Reveal } from "@/components/Reveal";
import {
  AI_PILLARS,
  AI_APPLICATIONS,
  AI_REALITY,
  LEARNING_TYPES,
  ALGORITHMS,
  AI_CHOICE,
  AI_CHOICE_QUESTIONS,
  AI_LIFECYCLE,
  DATA_TASKS,
  DATA_QUESTIONS,
  MODEL_EVALUATION,
  RESPONSIBLE_PRINCIPLES,
  AI_GLOSSARY,
  IMAGES,
} from "@/data/content";

const SECTIONS = [
  ["#ia-modalidades", "Modalidades"],
  ["#ia-aprendizaje", "Cómo aprende"],
  ["#ia-algoritmos", "Algoritmos"],
  ["#ia-elegir", "Cómo elegir"],
  ["#ia-datos", "Datos"],
  ["#ia-proyecto", "Proyecto"],
  ["#ia-evaluacion", "Evaluación"],
  ["#ia-responsable", "IA responsable"],
  ["#ia-sectores", "Sectores"],
  ["#ia-glosario", "Glosario"],
];

const card = "bg-[#242764] border border-[#3FE0F2]/15 rounded-2xl";

export default function WhatIsPage() {
  const [active, setActive] = useState(AI_PILLARS[0].id);
  const navigate = useNavigate();
  const pillar = AI_PILLARS.find((p) => p.id === active);

  return (
    <div className="bg-[#14163C] min-h-screen" data-testid="whatis-page">
      <Seo
        title="Qué es la Inteligencia Artificial: modalidades, algoritmos y datos | Cibermedida"
        description="Machine Learning, Deep Learning, lenguaje natural, visión artificial, IA generativa y predictiva explicados con ejemplos: tipos de aprendizaje, algoritmos, cómo elegir enfoque, calidad de datos, evaluación de modelos e IA responsable."
      />
      <Navbar />
      <main className="pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          {/* Modalidades */}
          <div id="ia-modalidades" className="grid lg:grid-cols-2 gap-14 items-start scroll-mt-24">
            <Reveal className="relative order-2 lg:order-1 lg:sticky lg:top-28">
              <div className="relative rounded-2xl overflow-hidden border border-[#3FE0F2]/20 scanline" data-testid="whatis-image-frame">
                <img src={IMAGES.code} alt="Modelos de inteligencia artificial en funcionamiento" width="1200" height="800" className="w-full h-[420px] object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#14163C]/80 via-transparent to-[#3FE0F2]/10" />
                <div className="absolute bottom-5 left-5 font-mono-cyber text-[11px] uppercase tracking-[0.2em] text-[#3FE0F2] bg-[#14163C]/80 backdrop-blur-sm border border-[#3FE0F2]/30 rounded-full px-4 py-2">
                  Modelos en producción
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-r-2 border-b-2 border-[#3FE0F2]/40 rounded-br-3xl pointer-events-none" />
            </Reveal>

            <div className="order-1 lg:order-2">
              <SectionHeading
                testId="whatis-heading"
                kicker="Inteligencia Artificial"
                title="¿Qué es realmente la IA aplicada a una organización?"
                description="No es una sola tecnología ni un chat de moda: es un campo formado por modalidades distintas, cada una con su finalidad, su forma de trabajar y sus aplicaciones. Bien elegidas y bien gobernadas, transforman cómo trabaja tu equipo."
              />
              <div className="flex flex-wrap gap-2 mb-6" data-testid="whatis-tabs">
                {AI_PILLARS.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`whatis-tab-${p.id}`}
                    onClick={() => setActive(p.id)}
                    className={`text-xs font-mono-cyber uppercase tracking-wider rounded-full px-4 py-2 border transition-all duration-300 ${
                      active === p.id
                        ? "bg-[#3FE0F2] text-[#14163C] border-[#3FE0F2] font-semibold"
                        : "text-[#B9C3EC] border-[#3FE0F2]/20 hover:border-[#3FE0F2]/60"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={pillar.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.35 }}
                  className={`${card} p-7`}
                  data-testid="whatis-tab-panel"
                >
                  <h2 className="font-heading text-xl font-medium text-[#3FE0F2]">{pillar.title}</h2>
                  <p className="mt-3 text-base text-[#B9C3EC] leading-relaxed">{pillar.text}</p>
                  {pillar.points && (
                    <ul className="mt-5 grid sm:grid-cols-2 gap-2.5">
                      {pillar.points.map((pt) => (
                        <li key={pt} className="flex gap-2.5 text-sm text-[#D8E2FF] leading-snug">
                          <CheckCircle2 size={16} className="text-[#06D6A0] shrink-0 mt-0.5" />
                          {pt}
                        </li>
                      ))}
                    </ul>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Índice de secciones */}
          <nav aria-label="Secciones de la página" className="mt-16 flex flex-wrap gap-2" data-testid="whatis-index">
            {SECTIONS.slice(1).map(([hash, label]) => (
              <button
                key={hash}
                type="button"
                onClick={() => scrollToSection(hash)}
                className="text-xs text-[#B9C3EC] border border-[#3FE0F2]/20 rounded-full px-4 py-2 hover:border-[#3FE0F2]/60 hover:text-[#3FE0F2] transition-colors duration-300"
              >
                {label}
              </button>
            ))}
          </nav>

          {/* Percibir, aprender, actuar */}
          <section className="mt-20" data-testid="whatis-reality">
            <Reveal>
              <div className="grid lg:grid-cols-5 gap-10 items-center">
                <div className="lg:col-span-2">
                  <span className="font-mono-cyber text-xs uppercase tracking-[0.25em] text-[#3FE0F2]">Expectativas reales</span>
                  <h2 className="font-heading mt-3 text-2xl sm:text-3xl font-semibold text-[#EEFBFF] leading-tight">{AI_REALITY.title}</h2>
                  <p className="mt-4 text-base text-[#B9C3EC] leading-relaxed">{AI_REALITY.text}</p>
                </div>
                <div className="lg:col-span-3 grid sm:grid-cols-3 gap-4">
                  {AI_REALITY.steps.map(([t, d], i) => (
                    <div key={t} className={`${card} p-6`}>
                      <span className="font-mono-cyber text-2xl font-semibold text-[#3FE0F2]/50">0{i + 1}</span>
                      <h3 className="font-heading mt-2 text-lg font-semibold text-[#EEFBFF]">{t}</h3>
                      <p className="mt-2 text-sm text-[#B9C3EC] leading-relaxed">{d}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </section>

          {/* Tipos de aprendizaje */}
          <section id="ia-aprendizaje" className="mt-28 scroll-mt-24" data-testid="whatis-learning">
            <SectionHeading
              kicker="Cómo aprende una máquina"
              title="Cuatro formas de aprender a partir de datos"
              description="El tipo de aprendizaje depende de los datos que tienes y de lo que quieres conseguir. Es la primera decisión técnica de cualquier proyecto."
            />
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {LEARNING_TYPES.map((l, i) => (
                <Reveal key={l.name} delay={i * 0.06}>
                  <div className={`card-glow h-full ${card} p-6 flex flex-col`}>
                    <h3 className="font-heading text-lg font-semibold text-[#3FE0F2]">{l.name}</h3>
                    <dl className="mt-4 space-y-3 text-sm flex-1">
                      <div>
                        <dt className="font-mono-cyber text-[10px] uppercase tracking-[0.2em] text-[#7D86C2]">Datos</dt>
                        <dd className="mt-1 text-[#D8E2FF] leading-snug">{l.data}</dd>
                      </div>
                      <div>
                        <dt className="font-mono-cyber text-[10px] uppercase tracking-[0.2em] text-[#7D86C2]">Objetivo</dt>
                        <dd className="mt-1 text-[#D8E2FF] leading-snug">{l.goal}</dd>
                      </div>
                    </dl>
                    <p className="mt-4 pt-4 border-t border-[#3FE0F2]/10 text-sm text-[#B9C3EC] leading-relaxed">{l.example}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>

          {/* Algoritmos */}
          <section id="ia-algoritmos" className="mt-28 scroll-mt-24" data-testid="whatis-algorithms">
            <SectionHeading
              kicker="Algoritmos"
              title="Lo que hay detrás de cada predicción"
              description="Algunos algoritmos clasifican, otros predicen, agrupan, recomiendan, detectan anomalías o generan contenido. Un mismo proyecto suele combinar varios, cada uno con una función distinta."
            />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {ALGORITHMS.map((a, i) => (
                <Reveal key={a.name} delay={Math.min(i * 0.03, 0.2)}>
                  <div className={`h-full ${card} p-6`}>
                    <span className="font-mono-cyber text-[10px] uppercase tracking-[0.2em] text-[#3FE0F2]">{a.family}</span>
                    <h3 className="font-heading mt-2 text-base font-semibold text-[#EEFBFF]">{a.name}</h3>
                    <p className="mt-2 text-sm text-[#B9C3EC] leading-relaxed">{a.use}</p>
                    <p className="mt-3 text-xs text-[#7D86C2] leading-relaxed">{a.note}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>

          {/* Cómo elegir */}
          <section id="ia-elegir" className="mt-28 scroll-mt-24" data-testid="whatis-choice">
            <SectionHeading
              kicker="Cómo elegir"
              title="La pregunta no es qué tecnología es más avanzada"
              description="Es qué problema quieres resolver y con qué datos cuentas. No todos los problemas necesitan redes neuronales: muchas veces una solución sencilla y fácil de explicar es la más adecuada."
            />
            <div className="grid lg:grid-cols-5 gap-8">
              <Reveal className="lg:col-span-3">
                <div className={`${card} overflow-hidden`}>
                  <div className="grid grid-cols-2 px-6 py-3 border-b border-[#3FE0F2]/15 font-mono-cyber text-[10px] uppercase tracking-[0.2em] text-[#7D86C2]">
                    <span>Si necesitas…</span>
                    <span>Enfoque habitual</span>
                  </div>
                  {AI_CHOICE.map(([need, approach]) => (
                    <div key={need} className="grid grid-cols-2 gap-4 px-6 py-4 border-b border-[#3FE0F2]/10 last:border-0 text-sm">
                      <span className="text-[#D8E2FF] leading-snug">{need}</span>
                      <span className="text-[#3FE0F2] leading-snug">{approach}</span>
                    </div>
                  ))}
                </div>
              </Reveal>
              <Reveal delay={0.1} className="lg:col-span-2">
                <div className="h-full bg-[#1B1E4E] border border-[#3FE0F2]/20 rounded-2xl p-7">
                  <h3 className="font-heading text-lg font-semibold text-[#EEFBFF]">Cinco preguntas antes de elegir</h3>
                  <ul className="mt-5 space-y-4">
                    {AI_CHOICE_QUESTIONS.map((q) => (
                      <li key={q} className="flex gap-3 text-sm text-[#B9C3EC] leading-relaxed">
                        <HelpCircle size={17} className="text-[#3FE0F2] shrink-0 mt-0.5" />
                        {q}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-6 pt-5 border-t border-[#3FE0F2]/15 text-sm text-[#D8E2FF] leading-relaxed">
                    A veces la conclusión honesta es que no hace falta IA: una regla de negocio o un análisis estadístico resuelve el problema con menos coste y más fácil mantenimiento.
                  </p>
                </div>
              </Reveal>
            </div>
          </section>

          {/* Datos */}
          <section id="ia-datos" className="mt-28 scroll-mt-24" data-testid="whatis-data">
            <SectionHeading
              kicker="Datos"
              title="Un modelo nunca es mejor que sus datos"
              description="Un proyecto de IA es, ante todo, un proyecto de gestión de información. Los datos rara vez llegan perfectos, y su preparación suele llevar más tiempo que entrenar el propio modelo."
            />
            <div className="grid lg:grid-cols-3 gap-8">
              <Reveal>
                <div className="h-full bg-[#1B1E4E] border border-[#3FE0F2]/20 rounded-2xl p-7">
                  <h3 className="font-heading text-lg font-semibold text-[#EEFBFF]">Las preguntas que importan</h3>
                  <ul className="mt-5 space-y-4">
                    {DATA_QUESTIONS.map((q) => (
                      <li key={q} className="flex gap-3 text-sm text-[#B9C3EC] leading-relaxed">
                        <HelpCircle size={17} className="text-[#3FE0F2] shrink-0 mt-0.5" />
                        {q}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-6 pt-5 border-t border-[#3FE0F2]/15 text-sm text-[#D8E2FF] leading-relaxed">
                    Estas preguntas son tan importantes como el algoritmo elegido: un modelo avanzado con datos mal preparados da resultados poco fiables.
                  </p>
                </div>
              </Reveal>
              <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
                {DATA_TASKS.map(([t, d], i) => (
                  <Reveal key={t} delay={i * 0.05}>
                    <div className={`h-full ${card} p-6`}>
                      <h3 className="font-heading text-base font-semibold text-[#3FE0F2]">{t}</h3>
                      <p className="mt-2 text-sm text-[#B9C3EC] leading-relaxed">{d}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          {/* Ciclo del proyecto */}
          <section id="ia-proyecto" className="mt-28 scroll-mt-24" data-testid="whatis-lifecycle">
            <SectionHeading
              kicker="Ciclo de un proyecto"
              title="Del problema de negocio al modelo vigilado"
              description="Seis fases iterativas: en cada paso se decide si los resultados bastan para continuar o conviene volver atrás. Así se evita avanzar sobre bases débiles, y ningún modelo pasa directamente del experimento a producción."
            />
            <ol className="grid sm:grid-cols-2 lg:grid-cols-6 gap-4">
              {AI_LIFECYCLE.map((s, i) => (
                <Reveal key={s.title} delay={i * 0.05}>
                  <li className={`relative h-full ${card} p-5`}>
                    <span className="font-mono-cyber text-xs text-[#14163C] bg-[#3FE0F2] rounded-full w-7 h-7 inline-flex items-center justify-center font-semibold">{i + 1}</span>
                    <h3 className="font-heading mt-4 text-sm font-semibold text-[#EEFBFF] leading-snug">{s.title}</h3>
                    <p className="mt-2 text-xs text-[#B9C3EC] leading-relaxed">{s.text}</p>
                  </li>
                </Reveal>
              ))}
            </ol>
          </section>

          {/* Evaluación */}
          <section id="ia-evaluacion" className="mt-28 scroll-mt-24" data-testid="whatis-evaluation">
            <SectionHeading
              kicker="Evaluación"
              title="Cómo saber si un modelo funciona de verdad"
              description="Las métricas dicen cuánto acierta un modelo; el análisis muestra dónde se equivoca, si sobreajusta y si algunos grupos reciben peores resultados."
            />
            <div className="grid sm:grid-cols-2 gap-5">
              {MODEL_EVALUATION.map((e, i) => (
                <Reveal key={e.title} delay={i * 0.06}>
                  <div className={`card-glow h-full ${card} p-7`}>
                    <h3 className="font-heading text-lg font-semibold text-[#EEFBFF]">{e.title}</h3>
                    <p className="mt-3 text-sm text-[#B9C3EC] leading-relaxed">{e.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>

          {/* IA responsable */}
          <section id="ia-responsable" className="mt-28 scroll-mt-24" data-testid="whatis-responsible">
            <div className="bg-[#1B1E4E] border border-[#3FE0F2]/20 rounded-3xl p-7 sm:p-12">
              <SectionHeading
                kicker="IA responsable"
                title="Técnicamente posible no siempre significa adecuado"
                description="La IA no es neutral: incorpora decisiones humanas en cada fase, desde qué datos se recogen hasta cómo se usan sus resultados. Un modelo se evalúa por su precisión, pero también por su impacto, transparencia, seguridad, privacidad y capacidad de revisión."
              />
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {RESPONSIBLE_PRINCIPLES.map(([t, d], i) => (
                  <Reveal key={t} delay={i * 0.05}>
                    <div className="h-full border-l-2 border-[#3FE0F2]/50 pl-5">
                      <h3 className="font-heading text-base font-semibold text-[#3FE0F2]">{t}</h3>
                      <p className="mt-2 text-sm text-[#B9C3EC] leading-relaxed">{d}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
              <Link
                to="/cumplimiento/ai-act"
                className="group mt-10 inline-flex items-center gap-2 text-sm font-semibold text-[#3FE0F2]"
              >
                Cómo lo aterrizamos en el AI Act
                <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
              </Link>
            </div>
          </section>

          {/* Sectores */}
          <section id="ia-sectores" className="mt-28 scroll-mt-24">
            <SectionHeading
              testId="whatis-applications-heading"
              kicker="Aplicaciones"
              title="La misma tecnología, realidades muy distintas"
              description="Un mismo problema técnico cambia según el sector: el coste de un error, la regulación y la exigencia de explicación no son iguales al detectar fraude que al prever la demanda."
            />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {AI_APPLICATIONS.map((a, i) => (
                <Reveal key={a.sector} delay={i * 0.06}>
                  <div data-testid={`whatis-application-${i}`} className={`card-glow h-full ${card} p-7`}>
                    <h3 className="font-heading text-lg font-semibold text-[#EEFBFF]">{a.sector}</h3>
                    <p className="mt-3 text-sm text-[#B9C3EC] leading-relaxed">{a.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>

          {/* Glosario */}
          <section id="ia-glosario" className="mt-28 scroll-mt-24" data-testid="whatis-glossary">
            <SectionHeading
              kicker="Glosario"
              title="El vocabulario esencial de la IA"
              description="Los términos que vas a oír en cualquier conversación sobre inteligencia artificial, explicados en una frase."
            />
            <dl className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-7">
              {AI_GLOSSARY.map(([term, def], i) => (
                <Reveal key={term} delay={Math.min(i * 0.02, 0.2)}>
                  <div>
                    <dt className="font-heading text-base font-semibold text-[#EEFBFF]">{term}</dt>
                    <dd className="mt-1.5 text-sm text-[#B9C3EC] leading-relaxed">{def}</dd>
                  </div>
                </Reveal>
              ))}
            </dl>
          </section>

          <Reveal delay={0.1} className="mt-24 text-center">
            <p className="text-base text-[#B9C3EC] max-w-2xl mx-auto mb-7 leading-relaxed">
              Cuéntanos qué decisión quieres mejorar y te diremos con franqueza qué enfoque tiene sentido, qué datos harían falta y qué retorno cabe esperar.
            </p>
            <button
              data-testid="whatis-cta"
              onClick={() => goToContact(navigate, "Consultoría e implantación de IA")}
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#14163C] bg-[#3FE0F2] rounded-full px-8 py-3.5 hover:bg-[#7FEBF8] hover:shadow-[0_0_28px_rgba(63,224,242,0.4)] transition-all duration-300 group"
            >
              Descubrir mi caso de uso
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          </Reveal>
        </div>
      </main>
      <Footer />
    </div>
  );
}
